import { Component, EventEmitter, Output, Input, AfterViewInit, ViewChild, ElementRef, OnDestroy, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

@Component({
  selector: 'app-chat-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <form [formGroup]="chatForm" (ngSubmit)="onSubmit()" class="flex items-start space-x-3 w-full">
      <div class="flex-1 relative w-full">
        <label for="query" class="sr-only">Your question</label>
         <textarea
           #textareaInput
           id="query"
           formControlName="query"
           rows="5"
           (keydown)="onKeyDown($event)"
           (input)="autoResize()"
           [disabled]="disabled || feedbackGiven"
           class="block w-full rounded-lg border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red resize-none text-sm sm:text-base min-h-20 sm:min-h-32"
           [ngClass]="{'bg-gray-100': feedbackGiven, 'opacity-70': feedbackGiven, 'cursor-not-allowed': feedbackGiven}"
           placeholder="What's your problem? Describe it here..."
           style="max-height: 33vh; overflow-y: auto;"
         ></textarea>
      </div>
      <div class="flex-shrink-0 self-end">
        <button
          type="submit"
          [disabled]="!chatForm.valid || disabled || feedbackGiven"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-vodafone-red hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vodafone-red disabled:opacity-50 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          Send
        </button>
      </div>
    </form>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      transition: height 0.2s ease-in-out;
    }
  `]
})
export class ChatInputComponent implements AfterViewInit, OnDestroy {
  @Input() disabled = false;
  @Input() feedbackGiven = false;
  @Output() sendMessage = new EventEmitter<string>();
  @Output() heightChange = new EventEmitter<number>();
  @ViewChild('textareaInput') textareaInput!: ElementRef;
  @HostBinding('style.height') componentHeight = 'auto';
  
  chatForm: FormGroup;
  private resizeObserver: ResizeObserver | null = null;
  private lastHeight = 0;

  constructor(private fb: FormBuilder) {
    this.chatForm = this.fb.group({
      query: ['', Validators.required],
    });
  }

  ngAfterViewInit() {
    // Set initial height
    setTimeout(() => this.autoResize(), 0);
    
    // Create a ResizeObserver to handle dynamic content changes
    if (typeof ResizeObserver !== 'undefined' && this.textareaInput?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        this.autoResize();
      });
      this.resizeObserver.observe(this.textareaInput.nativeElement);
    }
  }
  
  ngOnDestroy() {
    // Cleanup ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  autoResize() {
    const textarea = this.textareaInput?.nativeElement;
    if (!textarea) return;
    
    // Reset height temporarily to get the correct scrollHeight
    const originalOverflow = textarea.style.overflow;
    textarea.style.overflow = 'hidden';
    textarea.style.height = 'auto';
    
    // Set new height based on scrollHeight but limit to 1/3 of viewport height
    const maxHeight = window.innerHeight * 0.33;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = newHeight + 'px';
    
    // Restore overflow
    textarea.style.overflow = originalOverflow;
    
    // Calculate form height for parent container
    const form = textarea.closest('form');
    if (form) {
      const formHeight = form.offsetHeight;
      if (this.lastHeight !== formHeight) {
        this.lastHeight = formHeight;
        this.componentHeight = `${formHeight}px`;
        this.heightChange.emit(formHeight);
      }
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !this.disabled && !this.feedbackGiven) {
      event.preventDefault();
      this.onSubmit();
    }
  }

  onSubmit(): void {
    if (this.chatForm.valid && !this.disabled && !this.feedbackGiven) {
      this.sendMessage.emit(this.chatForm.get('query')?.value);
      this.chatForm.reset();
      
      // Reset height after submitting
      setTimeout(() => this.autoResize(), 0);
    }
  }
}